/**
 * Admin API Client — DevGPT v3.9.0
 * TypeScript client for admin portal endpoints
 * Supports legacy blob pipeline + SharePoint webhook architecture
 */

import type {
  SystemHealthResponse,
  PipelineStatusResponse,
  MetricsSummaryResponse,
  FeedbackStatsResponse,
  NegativeFeedbackItem,
  WebhookSubscriptionsResponse,
  IndexerInvocationsResponse,
  ActionResult,
  DiagnosticReport,
  DiagnosticHistoryResponse,
  GoldenQuery,
  GoldenQueriesResponse,
  ConversationListParams,
  ConversationListResponse,
  ConversationDetail,
  ConversationStatsResponse,
} from '../types/admin';
import { fetchWithAuth } from './fetchWithAuth';

const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL ||
  'https://devgpt-api.yellowbay-ae9e6304.eastus2.azurecontainerapps.io';

// ============================================================================
// Helpers
// ============================================================================

async function fetchJson<T>(url: string, init?: RequestInit): Promise<T> {
  const response = await fetchWithAuth(url, init);
  if (!response.ok) {
    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
  }
  return response.json() as Promise<T>;
}

// ============================================================================
// System Health
// ============================================================================

export async function getSystemHealth(): Promise<SystemHealthResponse> {
  return fetchJson<SystemHealthResponse>(`${API_BASE_URL}/admin/api/health`);
}

// ============================================================================
// Document Pipeline
// ============================================================================

export async function getPipelineStatus(): Promise<PipelineStatusResponse> {
  return fetchJson<PipelineStatusResponse>(`${API_BASE_URL}/admin/api/pipeline`);
}

// ============================================================================
// Usage Analytics
// ============================================================================

export async function getMetricsSummary(
  hours: number = 24
): Promise<MetricsSummaryResponse> {
  return fetchJson<MetricsSummaryResponse>(
    `${API_BASE_URL}/admin/api/metrics/summary?hours=${hours}`
  );
}

// ============================================================================
// Feedback
// ============================================================================

export async function getFeedbackStats(
  days: number = 30
): Promise<FeedbackStatsResponse> {
  return fetchJson<FeedbackStatsResponse>(
    `${API_BASE_URL}/api/feedback/stats?days=${days}`
  );
}

export async function getNegativeFeedback(
  limit: number = 20
): Promise<NegativeFeedbackItem[]> {
  return fetchJson<NegativeFeedbackItem[]>(
    `${API_BASE_URL}/api/feedback/negative?limit=${limit}`
  );
}

// ============================================================================
// Webhook Subscriptions
// ============================================================================

export async function getWebhookSubscriptions(): Promise<WebhookSubscriptionsResponse> {
  return fetchJson<WebhookSubscriptionsResponse>(
    `${API_BASE_URL}/admin/api/webhook-subscriptions`
  );
}

export async function renewWebhookSubscriptions(): Promise<ActionResult> {
  return fetchJson<ActionResult>(`${API_BASE_URL}/admin/api/webhook-renew`, {
    method: 'POST',
  });
}

// ============================================================================
// Indexer Operations
// ============================================================================

export async function getIndexerInvocations(): Promise<IndexerInvocationsResponse> {
  return fetchJson<IndexerInvocationsResponse>(
    `${API_BASE_URL}/admin/api/indexer-invocations`
  );
}

export async function triggerDeltaSync(siteLabel: string): Promise<ActionResult> {
  return fetchJson<ActionResult>(`${API_BASE_URL}/admin/api/delta-sync`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ site: siteLabel }),
  });
}

export async function reindexDocument(fileUrl: string): Promise<ActionResult> {
  return fetchJson<ActionResult>(`${API_BASE_URL}/admin/api/reindex-document`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ file_url: fileUrl }),
  });
}

// ============================================================================
// Quick Actions
// ============================================================================

export async function runDiagnostics(): Promise<ActionResult> {
  return fetchJson<ActionResult>(`${API_BASE_URL}/admin/api/actions/run-diagnostics`, {
    method: 'POST',
  });
}

export async function triggerIndexer(): Promise<ActionResult> {
  return fetchJson<ActionResult>(`${API_BASE_URL}/admin/api/actions/run-indexer`, {
    method: 'POST',
  });
}

export async function clearCache(): Promise<ActionResult> {
  return fetchJson<ActionResult>(`${API_BASE_URL}/admin/api/actions/clear-cache`, {
    method: 'POST',
  });
}

// ============================================================================
// Diagnostics (F18)
// ============================================================================

export async function runDiagnosticsFull(): Promise<DiagnosticReport> {
  return fetchJson<DiagnosticReport>(`${API_BASE_URL}/admin/api/diagnostics/run`, {
    method: 'POST',
  });
}

export async function getDiagnosticsHistory(limit = 10): Promise<DiagnosticHistoryResponse> {
  return fetchJson<DiagnosticHistoryResponse>(
    `${API_BASE_URL}/admin/api/diagnostics/history?limit=${limit}`,
  );
}

export async function getGoldenQueries(): Promise<GoldenQueriesResponse> {
  return fetchJson<GoldenQueriesResponse>(
    `${API_BASE_URL}/admin/api/diagnostics/golden-queries`,
  );
}

export async function saveGoldenQueries(queries: GoldenQuery[]): Promise<{ status: string; count: number }> {
  return fetchJson<{ status: string; count: number }>(
    `${API_BASE_URL}/admin/api/diagnostics/golden-queries`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ queries }),
    },
  );
}

// ============================================================================
// Conversation Review (F19)
// ============================================================================

export async function getConversations(
  params: ConversationListParams = {}
): Promise<ConversationListResponse> {
  const qs = new URLSearchParams();
  // Append only params the caller set. The guard is intentionally
  // `=== undefined || === ''` and NOT `if (!value)` — offset=0 is a valid
  // value; truthiness would silently drop it and let the backend re-default.
  const entries: Array<[string, string | number | undefined]> = [
    ['user', params.user],
    ['from', params.from],
    ['to', params.to],
    ['search', params.search],
    ['limit', params.limit],
    ['offset', params.offset],
  ];
  for (const [key, value] of entries) {
    if (value === undefined || value === '') continue;
    qs.append(key, String(value));
  }
  const query = qs.toString();
  return fetchJson<ConversationListResponse>(
    `${API_BASE_URL}/admin/api/conversations${query ? `?${query}` : ''}`
  );
}

export async function getConversationDetail(
  chatId: string
): Promise<ConversationDetail> {
  return fetchJson<ConversationDetail>(
    `${API_BASE_URL}/admin/api/conversations/${encodeURIComponent(chatId)}`
  );
}

export async function getConversationStats(
  days: number = 30
): Promise<ConversationStatsResponse> {
  return fetchJson<ConversationStatsResponse>(
    `${API_BASE_URL}/admin/api/conversations/stats?days=${days}`
  );
}

// Re-export types for convenience
export type {
  SystemHealthResponse,
  PipelineStatusResponse,
  MetricsSummaryResponse,
  FeedbackStatsResponse,
  WebhookSubscriptionsResponse,
  IndexerInvocationsResponse,
  ActionResult,
  DiagnosticReport,
  DiagnosticHistoryResponse,
  GoldenQuery,
  GoldenQueriesResponse,
  ConversationListParams,
  ConversationListResponse,
  ConversationDetail,
  ConversationStatsResponse,
};
